import { Link, useRouterState } from "@tanstack/react-router";
import { useMutation } from "convex/react";
import { useQuery } from "@/lib/convex";
import { LuLogIn, LuLogOut, LuUserRound } from "react-icons/lu";
import type { ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Box,
  Button,
  Card,
  Center,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Stack,
  Text,
} from "@chakra-ui/react";
import { api } from "../convex/_generated/api";
import type { Id } from "../convex/_generated/dataModel";
import { useEffectivePersonId } from "./impersonation";

const entraAccessScope = `${process.env.PUBLIC_AZURE_AUTH_AUDIENCE as string}/access_as_user`;

type Viewer = {
  tokenIdentifier: string;
  subject?: string;
  issuer?: string;
  name?: string | null;
  givenName?: string | null;
  familyName?: string | null;
  nickname?: string | null;
  preferredUsername?: string | null;
  email?: string | null;
};

type AuthState = {
  isAuthenticated: boolean;
  isPending: boolean;
  viewer: Viewer | null | undefined;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
};

export function useMicrosoftEntraAuth(): AuthState {
  const { instance, inProgress, accounts } = useMsal();
  const activeAccount = instance.getActiveAccount() ?? accounts[0] ?? null;
  const isAuthenticated = activeAccount !== null;
  const viewer = useQuery((api as typeof api & { auth: { viewer: unknown } }).auth.viewer as never, isAuthenticated ? {} : "skip") as
    | Viewer
    | null
    | undefined;
  const ensureViewerLink = useMutation(api.persons.ensureViewerLink);

  useEffect(() => {
    if (!activeAccount || !viewer) {
      return;
    }

    const entraIdFromClaims =
      typeof activeAccount.localAccountId === "string" && activeAccount.localAccountId.length > 0
        ? activeAccount.localAccountId
        : typeof activeAccount.idTokenClaims?.oid === "string"
          ? activeAccount.idTokenClaims.oid
          : null;

    if (!entraIdFromClaims) {
      return;
    }

    void ensureViewerLink({ entraId: entraIdFromClaims });
  }, [activeAccount, ensureViewerLink, viewer]);

  return useMemo(
    () => ({
      isAuthenticated,
      isPending: inProgress !== "none" || (isAuthenticated && viewer === undefined),
      viewer,
      async signIn() {
        await instance.loginRedirect({ scopes: [entraAccessScope] });
      },
      async signOut() {
        await instance.logoutRedirect({
          account: activeAccount ?? undefined,
          postLogoutRedirectUri: process.env.PUBLIC_CEMMBASE_URL as string,
        });
      },
    }),
    [activeAccount, inProgress, instance, isAuthenticated, viewer],
  );
}

export function AuthActions() {
  const { isAuthenticated, isPending, viewer, signIn, signOut } = useMicrosoftEntraAuth();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const effectivePersonId = useEffectivePersonId();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const profile = useQuery(
    api.persons.viewerProfile,
    isAuthenticated ? { effectivePersonId: effectivePersonId as Id<"persons"> | null } : "skip",
  );
  const impersonating = profile !== undefined && profile !== null && "impersonation" in profile
    && profile.impersonation !== null && profile.impersonation.active
    ? profile.impersonation
    : null;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  if (isPending) {
    return <Text display="block" px={3} py={2.5} fontSize="sm" color="fg.muted">Authenticating…</Text>;
  }

  if (!isAuthenticated) {
    return (
      <Button colorPalette="blue" size="sm" w="full" onClick={() => void signIn()}>
        <LuLogIn />
        Sign in with Microsoft
      </Button>
    );
  }

  // The name of whoever is being LOOKED AS, not whoever signed in. The
  // profile link beside it goes to their profile, so a footer reading the
  // admin's own name was describing a page it did not lead to.
  const displayName = impersonating?.effectiveFullName
    ?? viewer?.name
    ?? viewer?.givenName
    ?? viewer?.preferredUsername
    ?? "User";
  const isActive = pathname === "/profile";

  return (
    <HStack
      gap={3}
      px={5}
      py={3}
      fontSize="0.95rem"
      fontWeight="medium"
      whiteSpace="nowrap"
      borderYWidth="1px"
      borderColor={isActive ? "border" : "transparent"}
      bg={isActive ? "bg.muted" : "transparent"}
      color={isActive ? "fg" : "fg.muted"}
    >
      <Icon as={LuUserRound} boxSize={5} flexShrink={0} color={isActive ? "blue.500" : "fg.muted"} />
      <Box minW={0} flex="1" truncate>
        <Link to="/profile">{displayName}</Link>
      </Box>
      <IconButton
        aria-label="Sign out"
        size="xs"
        variant="ghost"
        color="fg.muted"
        onClick={() => void signOut()}
      >
        <LuLogOut />
      </IconButton>
    </HStack>
  );
}

export function AuthGate({ children }: { children: ReactNode }) {
  const { isAuthenticated, isPending, signIn } = useMicrosoftEntraAuth();

  if (isPending) {
    return (
      <Center minH="100vh" bg="bg.subtle" px={6}>
        <Card.Root maxW="lg" borderWidth="1px" borderColor="border" shadow="sm">
          <Card.Body gap={3} textAlign="center">
            <Heading size="xl">Checking your session</Heading>
            <Text fontSize="sm" color="fg.muted">
              CeMMbase is verifying your Microsoft Entra sign-in and establishing an authenticated session.
            </Text>
          </Card.Body>
        </Card.Root>
      </Center>
    );
  }

  if (!isAuthenticated) {
    return (
      <Center minH="100vh" bg="bg.subtle" px={6}>
        <Card.Root maxW="xl" borderWidth="1px" borderColor="border" shadow="sm">
          <Card.Body gap={5}>
            <Stack gap={2} textAlign="center">
              <Heading size="2xl">Sign in to CeMMbase</Heading>
            </Stack>
            <Flex justify="center">
              <Button colorPalette="blue" px={10} onClick={() => void signIn()}>
                <LuLogIn />
                Use your CeMM account to log in
              </Button>
            </Flex>
          </Card.Body>
        </Card.Root>
      </Center>
    );
  }

  return <>{children}</>;
}
